import Heading from "components/Typography/Heading"
import { memo, useCallback, useEffect, useMemo } from "react"
import styled from "styled-components"
import SelectV2, { Option } from "components/EvtKit/select/SelectV2"
import { BsArrowClockwise } from "react-icons/bs"
import Caption from "components/Typography/Caption"
import { productsMapperSelect } from "utils/mapper/event-types"
import { useAppSelector } from "reducers/store"
import { useQueryState } from "context/queryState"
import { SET_PRODUCT_LIST } from "reducers/products/actions-types"
import ApiProducts from "api/ApiProducts"
import { useDebouncedCallback } from "use-debounce"
import { initialDebouncedFilters, initialFilters } from "./Metrics"
import { PeriodFilters } from "constants/metrics"
import PeriodDatePicker from "components/EvtKit/period-date-picker/PeriodDatePicker"

const ContainerTitle = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`

const ClearFiltersContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  flex-direction: row;
  cursor: pointer;
  gap: 4px;
  padding: 8px 20px 8px 20px;
  border-radius: 40px;
  border: 1px solid;

  color: ${(props) => props.theme.palette.primary_one};

  &.disabled {
    color: ${(props) => props.theme.palette.disabled};
    cursor: not-allowed;
  }
`

const ClearFiltersIconContainer = styled.div`
  font-size: 20px;
  transform: rotate(45deg);
  color: ${(props) => props.theme.palette.primary_one};

  &.disabled {
    color: ${(props) => props.theme.palette.disabled};
  }
`

const ContainerFilters = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  width: 100%;
  padding: 6px 24px 0 0;
  align-items: center;
  justify-content: end;
`

const MetricsFilterSelectContainer = styled.div`
  max-width: 228px;
  width: 100%;
  display: flex;
`

interface Props {
  filters: App.MetricsParams
  debouncedFilters: App.MetricsParams
  periodFilter: PeriodFilters
  setFilters: React.Dispatch<React.SetStateAction<App.MetricsParams>>
  setDebouncedFilters: React.Dispatch<React.SetStateAction<App.MetricsParams>>
  setPeriodFilter: React.Dispatch<React.SetStateAction<PeriodFilters>>
}

const MetricsHeader = (props: Props) => {
  const { filters, debouncedFilters, periodFilter, setFilters, setDebouncedFilters, setPeriodFilter } = props

  const debouncedFiltersCallback = useDebouncedCallback(() => {
    setFilters((state) => ({
      ...state,
      ...debouncedFilters
    }))
  }, 1000)

  useEffect(() => {
    debouncedFiltersCallback()
  }, [debouncedFilters, debouncedFiltersCallback])

  useQueryState([SET_PRODUCT_LIST], async () => (await new ApiProducts().getAll()).products, SET_PRODUCT_LIST, {
    refetchOnMount: "always"
  })

  const { productList } = useAppSelector((state) => state.product)

  const handleFiltersChange = useCallback((key: string, value: string) => {
    setFilters((prevState) => ({
      ...prevState,
      [key]: value || undefined
    }))
  }, [])

  const handleProductChange = useCallback((option: Option | null) => {
    handleFiltersChange("productPrefixUrl", option?.value || "")
  }, [])

  const handleDateFilterChange = useCallback((date: [Date | null, Date | null]) => {
    setDebouncedFilters((prevState) => ({
      ...prevState,
      dateStart: date[0] || undefined,
      dateEnd: date[1] || undefined
    }))
  }, [])

  const clearFilters = useCallback(() => {
    setFilters(initialFilters)
    setDebouncedFilters(initialDebouncedFilters)
    setPeriodFilter(PeriodFilters.THIRTY_DAYS)
  }, [])

  const enableClearFilter = useMemo(() => Object.values(filters).some((item) => !!item), [filters])

  const getDataToProducts = useCallback(async () => {
    const prodOptions = productsMapperSelect(productList.data)

    return {
      hasMore: false,
      results: prodOptions
    }
  }, [productList])

  const handlePeriodChange = useCallback(
    (newPeriod: PeriodFilters) => {
      setPeriodFilter(newPeriod)
    },
    [setPeriodFilter]
  )

  return (
    <>
      <ContainerTitle>
        <Heading variant="h3">Métricas</Heading>
        <ContainerFilters>
          {enableClearFilter && (
            <ClearFiltersContainer onClick={clearFilters}>
              <ClearFiltersIconContainer>
                <BsArrowClockwise />
              </ClearFiltersIconContainer>
              <Caption size="large" color="primary-dark" weight="semi-bold">
                Limpar Filtros
              </Caption>
            </ClearFiltersContainer>
          )}
          {!enableClearFilter && (
            <ClearFiltersContainer className="disabled">
              <ClearFiltersIconContainer className="disabled">
                <BsArrowClockwise />
              </ClearFiltersIconContainer>
              <Caption size="large" color="gray-light" weight="semi-bold">
                Limpar Filtros
              </Caption>
            </ClearFiltersContainer>
          )}
          <MetricsFilterSelectContainer>
            <SelectV2
              id="product"
              label="Produtos"
              onChange={handleProductChange}
              placeholder="Visão Geral"
              shrink
              getData={getDataToProducts}
              value={filters.productPrefixUrl || ""}
              className="fullWidth"
            />
          </MetricsFilterSelectContainer>
          <PeriodDatePicker
            periodFilter={periodFilter}
            dateStart={debouncedFilters.dateStart}
            dateEnd={debouncedFilters.dateEnd}
            onPeriodChange={handlePeriodChange}
            onDateChange={handleDateFilterChange}
          />
        </ContainerFilters>
      </ContainerTitle>
    </>
  )
}

export default memo(MetricsHeader)
